<template>
  <div class="home-page" :style="pageStyle" v-loading="loading">
    <!-- 加载失败提示 -->
    <div v-if="loadError" class="error-tip">
      <p>配置加载失败，请刷新重试</p>
      <button @click="loadConfig">重试</button>
    </div>

    <!-- 动态渲染组件 -->
    <template v-else-if="pageConfig">
      <DynamicComponent
        v-for="component in visibleComponents"
        :key="component.id"
        :config="component"
        @action="handleComponentAction"
      />
    </template>

    <!-- 底部占位 -->
    <div class="bottom-placeholder"></div>
  </div>
</template>

<script>
import DynamicComponent from '../components/DynamicComponent.vue'
import { defaultConfig } from '../config/pageConfig'
import { trackMixin } from '@/mixins/trackMixin'
import { get } from '@/util/request'

export default {
  mixins: [trackMixin],
  name: 'HomePage',
  components: {
    DynamicComponent
  },
  data() {
    return {
      pageConfig: null,
      loading: false,
      loadError: false
    }
  },
  computed: {
    // 从 URL 参数获取页面编码
    pageCode() {
      // 支持两种方式：
      // 1. URL 查询参数：?pageCode=health_welfare_h5
      // 2. URL 路径参数：/page/health_welfare_h5
      return this.$route.query.pageCode ||
             this.$route.params.pageCode ||
             'health_welfare_h5' // 默认页面编码
    },
    // 页面样式
    pageStyle() {
      if (!this.pageConfig?.page) return {}
      const { backgroundImage, backgroundColor } = this.pageConfig.page
      return {
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundColor: backgroundColor || '#fff',
        backgroundSize: '100% auto'
      }
    },
    // 可见的组件列表
    visibleComponents() {
      return this.pageConfig?.components?.filter(c => c.visible !== false) || []
    }
  },
  watch: {
    // 监听页面编码变化，重新加载配置
    pageCode: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.waitForMonitoAndLoad()
        }
      }
    }
  },
  created() {
    this.waitForMonitoAndLoad()
  },
  methods: {
    // 等待 monitoAction 初始化完成后加载配置
    async waitForMonitoAndLoad() {
      // 等待 monitoAction 初始化完成
      await this.waitForMonitoAction()
      // 然后加载配置
      await this.loadConfig()
    },

    // 等待 monitoAction 初始化
    waitForMonitoAction() {
      return new Promise((resolve) => {
        // 如果已经初始化完成，直接返回
        if (this.$monitoAction) {
          resolve()
          return
        }

        // 最多等待 5 秒
        let attempts = 0
        const maxAttempts = 50 // 50 * 100ms = 5s
        const checkInterval = setInterval(() => {
          attempts++
          if (this.$monitoAction) {
            clearInterval(checkInterval)
            resolve()
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval)
            console.warn('monitoAction 初始化超时，继续加载页面配置')
            resolve()
          }
        }, 100)
      })
    },

    // 从 API 加载配置
    async loadConfig() {
      this.loading = true
      this.loadError = false

      try {
        // 调用 API 获取页面配置
        const response = await this.fetchPageConfig(this.pageCode)

        if (response.code === 200 && response.data) {
          this.pageConfig = response.data

          // 设置页面标题
          if (this.pageConfig?.page?.title) {
            document.title = this.pageConfig.page.title
          }
        } else {
          throw new Error(response.message || '获取配置失败')
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        this.loadError = true

        // 使用默认配置作为降级方案
        this.pageConfig = defaultConfig

        // 显示错误提示（可选）
        this.$toast && this.$toast('配置加载失败，使用默认配置')
      } finally {
        this.loading = false
      }
    },

    // 获取页面配置 API
    async fetchPageConfig(pageCode) {
      // 获取 URL 参数中的版本号和 A/B 测试分组
      const params = {
        version: this.$route.query.version,
        abTestGroup: this.$route.query.abTestGroup
      }

      // 使用 request.js 发送请求
      return await get(`/api/public/${pageCode}`, params)
    },

    // 处理组件事件
    handleComponentAction({ componentId, componentType, ...action }) {
      console.log('组件事件:', componentId, componentType, action)

      // 可以在这里处理全局事件，如统计、跳转等
      // 例如：上报组件交互数据
      this.reportComponentAction(componentId, componentType, action)
    },

    // 上报组件交互数据（可选）
    async reportComponentAction(componentId, componentType, action) {
      try {
        // 发送统计请求
        // await this.$http.post('/api/v1/page-config/action-log', {
        //   pageCode: this.pageCode,
        //   componentId,
        //   componentType,
        //   action,
        //   timestamp: new Date().toISOString()
        // })
      } catch (error) {
        console.error('上报组件事件失败:', error)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.home-page {
  position: relative;
  min-height: 100vh;
  padding-bottom: 0.5rem;
}

.bottom-placeholder {
  height: 0.5rem;
}

.error-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 0.5rem;

  p {
    color: #666;
    font-size: 0.28rem;
    margin-bottom: 0.3rem;
  }

  button {
    padding: 0.15rem 0.4rem;
    background: #E7401E;
    color: #fff;
    border: none;
    border-radius: 0.3rem;
    font-size: 0.28rem;
    cursor: pointer;

    &:active {
      opacity: 0.8;
    }
  }
}
</style>
